/**
 * Embed Auth Provider
 * Manages authentication state for the embedded app
 *
 * Features:
 * - Token expiry detection with proactive refresh
 * - Race condition prevention for concurrent refresh requests
 * - Exponential backoff for failed refreshes
 * - Automatic logout when refresh token expires
 * - Page visibility handling for background tabs
 */

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { debugLog } from '../debug';

// ============================================================================
// Types
// ============================================================================

export interface User {
  id: string;
  email: string;
  fullName: string;
  displayPic?: string;
  headline?: string;
  city?: string;
  country?: string;
  handle?: string;
}

export interface ProfileDetails {
  id: string;
  Name: string;
  Email: string;
  DisplayPic: string;
  Headline?: string;
  City?: string;
  Country?: string;
  BackgroundColor?: string;
  Handle?: string;
}

export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  jwt: string | null;
  refreshToken: string | null;
  profileLoading: boolean;
  guestToken: string | null;
  guestTokenLoading: boolean;
  /** Whether a token refresh is currently in progress */
  isRefreshing: boolean;
  /** Token expiry time (Unix timestamp in seconds) */
  tokenExpiryTime: number | null;
  /** Whether the token has expired */
  isTokenExpired: boolean;
}

export interface AuthContextValue extends AuthState {
  login: (data: LoginData) => void;
  logout: (reason?: string) => void;
  updateUser: (user: Partial<User>) => void;
  refreshAuth: () => Promise<boolean>;
  getToken: () => string | null;
  getGuestToken: () => string | null;
  fetchProfile: () => Promise<void>;
  refetchGuestToken: () => Promise<void>;
}

export interface LoginData {
  jwt: string;
  refreshToken: string;
  user: User;
}

// ============================================================================
// Constants
// ============================================================================

/** Storage key for auth data */
const STORAGE_KEY = 'bolder_widget_auth';

/** Simple obfuscation key (not true encryption, but prevents casual inspection) */
const OBFUSCATION_KEY = 'bolder_embed_v1';

/** Refresh token before expiry (in seconds) - 5 minutes */
const REFRESH_THRESHOLD_SECONDS = 5 * 60;

/** Check token status interval (in milliseconds) - 1 minute */
const TOKEN_CHECK_INTERVAL_MS = 60 * 1000;

/** Maximum consecutive refresh failures before logout */
const MAX_REFRESH_FAILURES = 3;

/** Base backoff time for retry (in milliseconds) */
const BASE_BACKOFF_MS = 1000;

/** Maximum backoff time (in milliseconds) - 30 seconds */
const MAX_BACKOFF_MS = 30 * 1000;

// ============================================================================
// Token Refresh Manager (Race Condition Prevention)
// ============================================================================

interface RefreshState {
  promise: Promise<boolean> | null;
  consecutiveFailures: number;
  lastFailureTime: number | null;
  isBlocked: boolean;
}

const refreshState: RefreshState = {
  promise: null,
  consecutiveFailures: 0,
  lastFailureTime: null,
  isBlocked: false,
};

/**
 * Calculates backoff time with exponential increase
 */
function getBackoffTime(failureCount: number): number {
  const backoff = BASE_BACKOFF_MS * Math.pow(2, failureCount - 1);
  return Math.min(backoff, MAX_BACKOFF_MS);
}

/**
 * Checks if refresh is currently blocked due to too many failures
 */
function isRefreshBlocked(): boolean {
  if (!refreshState.isBlocked) return false;

  // Check if backoff period has passed
  if (refreshState.lastFailureTime) {
    const backoffTime = getBackoffTime(refreshState.consecutiveFailures);
    const timeSinceFailure = Date.now() - refreshState.lastFailureTime;
    if (timeSinceFailure >= backoffTime) {
      refreshState.isBlocked = false;
      return false;
    }
  }

  return true;
}

/**
 * Records a successful refresh
 */
function recordRefreshSuccess(): void {
  refreshState.consecutiveFailures = 0;
  refreshState.lastFailureTime = null;
  refreshState.isBlocked = false;
  refreshState.promise = null;
}

/**
 * Records a failed refresh
 */
function recordRefreshFailure(): boolean {
  refreshState.consecutiveFailures++;
  refreshState.lastFailureTime = Date.now();
  refreshState.promise = null;

  // Block after max failures
  if (refreshState.consecutiveFailures >= MAX_REFRESH_FAILURES) {
    refreshState.isBlocked = true;
    return true; // Should logout
  }

  return false;
}

/**
 * Resets the refresh state (on logout or login)
 */
function resetRefreshState(): void {
  refreshState.promise = null;
  refreshState.consecutiveFailures = 0;
  refreshState.lastFailureTime = null;
  refreshState.isBlocked = false;
}

// ============================================================================
// Security Helpers
// ============================================================================

/**
 * Simple obfuscation for stored auth data.
 * This is NOT cryptographic security - it just prevents casual inspection.
 */
function obfuscate(data: string): string {
  try {
    const encoded = btoa(encodeURIComponent(data));
    let result = '';
    for (let i = 0; i < encoded.length; i++) {
      const charCode = encoded.charCodeAt(i) ^ OBFUSCATION_KEY.charCodeAt(i % OBFUSCATION_KEY.length);
      result += String.fromCharCode(charCode);
    }
    return btoa(result);
  } catch {
    return data;
  }
}

/**
 * Deobfuscate stored auth data.
 */
function deobfuscate(data: string): string {
  try {
    const decoded = atob(data);
    let result = '';
    for (let i = 0; i < decoded.length; i++) {
      const charCode = decoded.charCodeAt(i) ^ OBFUSCATION_KEY.charCodeAt(i % OBFUSCATION_KEY.length);
      result += String.fromCharCode(charCode);
    }
    return decodeURIComponent(atob(result));
  } catch {
    return data;
  }
}

// ============================================================================
// Context
// ============================================================================

const AuthContext = createContext<AuthContextValue | null>(null);

// ============================================================================
// Storage Helpers
// ============================================================================

function getStoredAuth(): LoginData | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const deobfuscated = deobfuscate(stored);
        return JSON.parse(deobfuscated);
      } catch {
        // Fall back to plain JSON (old format)
        return JSON.parse(stored);
      }
    }
  } catch {
    // Silent fail
  }
  return null;
}

function storeAuth(data: LoginData): void {
  try {
    const json = JSON.stringify(data);
    localStorage.setItem(STORAGE_KEY, obfuscate(json));
  } catch {
    // Silent fail
  }
}

function clearStoredAuth(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Silent fail
  }
}

// ============================================================================
// Token Helpers
// ============================================================================

interface TokenPayload {
  exp?: number;
  iat?: number;
  sub?: string;
  [key: string]: unknown;
}

/**
 * Decodes and parses a JWT token payload
 */
function parseToken(token: string): TokenPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    return payload;
  } catch {
    return null;
  }
}

/**
 * Gets the expiry time from a token (Unix timestamp in seconds)
 */
function getTokenExpiry(token: string): number | null {
  const payload = parseToken(token);
  return payload?.exp || null;
}

/**
 * Checks if a token is expired
 */
function isTokenExpired(token: string): boolean {
  const expiry = getTokenExpiry(token);
  if (!expiry) return true;
  const now = Math.floor(Date.now() / 1000);
  return expiry <= now;
}

/**
 * Checks if a token is expiring soon (within threshold)
 */
function isTokenExpiringSoon(token: string, thresholdSeconds = REFRESH_THRESHOLD_SECONDS): boolean {
  const expiry = getTokenExpiry(token);
  if (!expiry) return true;
  const now = Math.floor(Date.now() / 1000);
  return expiry - now < thresholdSeconds;
}

/**
 * Gets seconds until token expires
 */
function getSecondsUntilExpiry(token: string): number {
  const expiry = getTokenExpiry(token);
  if (!expiry) return 0;
  const now = Math.floor(Date.now() / 1000);
  return Math.max(0, expiry - now);
}

// ============================================================================
// API Helpers
// ============================================================================

async function fetchGuestToken(appBaseUrl?: string): Promise<string | null> {
  if (!appBaseUrl) return null;

  try {
    const url = `${appBaseUrl}/api/tenant/jwt`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'X-Bolder-Widget': 'true',
        'X-Bolder-Widget-Origin': typeof window !== 'undefined' ? window.location.origin : '',
      },
    });

    const result = await response.json();
    if (!response.ok) return null;
    return result.jwt || null;
  } catch {
    return null;
  }
}

interface HostSessionResponse {
  loggedIn?: boolean;
  jwt?: string | null;
  refreshToken?: string | null;
  email?: string;
  userId?: string;
  name?: string;
  handle?: string;
  userDetails?: {
    id?: string;
    email?: string;
    name?: string;
    handle?: string;
    displayPic?: string;
    DisplayPic?: string;
  };
}

function isSameOrigin(appBaseUrl: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return new URL(appBaseUrl, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Bridge the host app's iron-session into the widget on first load. Cross-
 * origin embeds can't share the cookie (CORS-credentialed preflight would
 * fail), so we skip the call entirely there and let the guest-token path
 * handle anonymous chat as today.
 */
async function fetchHostSession(appBaseUrl?: string): Promise<LoginData | null> {
  if (!appBaseUrl || !isSameOrigin(appBaseUrl)) return null;

  try {
    const response = await fetch(`${appBaseUrl}/api/auth/session`, {
      method: 'GET',
      credentials: 'include',
    });
    if (!response.ok) return null;
    const data = (await response.json()) as HostSessionResponse;
    if (!data?.loggedIn || !data?.jwt) return null;
    const details = data.userDetails ?? {};
    return {
      jwt: data.jwt,
      // Don't copy the host's refresh token — it's rotation-locked to the
      // host iron-session and will conflict if both clients try to use it.
      // Empty string marks this as a bridged session; refreshAuth detects
      // that and re-bridges (re-reads /api/auth/session) instead of POSTing
      // the host refresh token to GraphQL.
      refreshToken: '',
      user: {
        id: data.userId ?? details.id ?? '',
        email: data.email ?? details.email ?? '',
        fullName: data.name ?? details.name ?? data.email ?? '',
        displayPic: details.displayPic ?? details.DisplayPic,
        handle: data.handle ?? details.handle,
      },
    };
  } catch {
    return null;
  }
}

interface RefreshTokenResult {
  success: boolean;
  jwt?: string;
  refreshToken?: string;
  errorType?: 'network' | 'invalid_token' | 'expired_refresh_token' | 'unknown';
}

/**
 * Refreshes the JWT token using the refresh token
 */
async function refreshTokenApi(
  appBaseUrl: string,
  currentRefreshToken: string,
  expiredJwt?: string,
): Promise<RefreshTokenResult> {
  try {
    const url = `${appBaseUrl}/api/widget-proxy/auth/refresh`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Bolder-Widget': 'true',
      'X-Bolder-Widget-Origin': typeof window !== 'undefined' ? window.location.origin : '',
    };

    // Include expired JWT for context (some backends use this)
    if (expiredJwt) {
      headers['Authorization'] = `Bearer ${expiredJwt}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ refreshToken: currentRefreshToken }),
    });

    const result = await response.json();

    // Check for error responses
    if (!response.ok) {
      const errorMessage = result.error?.toLowerCase() || '';

      // Check for refresh token expiry
      if (errorMessage === 'refresh_token_expired' || errorMessage.includes('expired')) {
        return { success: false, errorType: 'expired_refresh_token' };
      }

      if (errorMessage.includes('invalid') || errorMessage.includes('unauthorized')) {
        return { success: false, errorType: 'invalid_token' };
      }

      return { success: false, errorType: 'unknown' };
    }

    // Check for successful response
    if (result.jwt) {
      return {
        success: true,
        jwt: result.jwt,
        refreshToken: result.refreshToken || currentRefreshToken,
      };
    }

    return { success: false, errorType: 'unknown' };
  } catch (error) {
    // Network error - might be temporary
    return { success: false, errorType: 'network' };
  }
}

async function fetchUserProfile(
  appBaseUrl: string,
  userId: string,
  token: string,
): Promise<ProfileDetails | null> {
  try {
    const url = `${appBaseUrl}/api/widget-proxy/auth/profile?userId=${encodeURIComponent(userId)}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Bolder-Widget': 'true',
        'X-Bolder-Widget-Origin': typeof window !== 'undefined' ? window.location.origin : '',
      },
    });

    if (!response.ok) {
      return null;
    }

    const result = await response.json();
    if (result.ProfileDetails) {
      return result.ProfileDetails;
    }
    return null;
  } catch {
    return null;
  }
}

// ============================================================================
// Provider
// ============================================================================

export interface EmbedAuthProviderProps {
  appBaseUrl: string;
  children: React.ReactNode;
  onAuthChange?: (isAuthenticated: boolean) => void;
  /** Called when session expires and user is logged out */
  onSessionExpired?: () => void;
}

export const EmbedAuthProvider: React.FC<EmbedAuthProviderProps> = ({
  appBaseUrl,
  children,
  onAuthChange,
  onSessionExpired,
}) => {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    jwt: null,
    refreshToken: null,
    profileLoading: false,
    guestToken: null,
    guestTokenLoading: true,
    isRefreshing: false,
    tokenExpiryTime: null,
    isTokenExpired: false,
  });

  // Refs for managing refresh and visibility
  const isRefreshingRef = useRef(false);
  const visibilityCheckScheduled = useRef(false);

  // ============================================================================
  // Logout Function
  // ============================================================================

  const logout = useCallback(
    (reason?: string) => {
      debugLog('[EmbedAuthProvider] logout() called with reason:', reason);

      clearStoredAuth();
      resetRefreshState();

      setState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        jwt: null,
        refreshToken: null,
        profileLoading: false,
        guestToken: null,
        guestTokenLoading: true, // Will refetch
        isRefreshing: false,
        tokenExpiryTime: null,
        isTokenExpired: false,
      });

      debugLog('[EmbedAuthProvider] logout() - dispatching auth change event (false)');
      onAuthChange?.(false);
      // Also dispatch a DOM event for components that can't receive callbacks
      window.dispatchEvent(new CustomEvent('bolder-auth-change', { detail: { isAuthenticated: false } }));

      // Notify about session expiry if that's the reason
      if (reason === 'session_expired' || reason === 'refresh_failed') {
        onSessionExpired?.();
      }

      // Refetch guest token for email login
      if (appBaseUrl) {
        fetchGuestToken(appBaseUrl).then((token) => {
          setState((prev) => ({
            ...prev,
            guestToken: token,
            guestTokenLoading: false,
          }));
          debugLog('[EmbedAuthProvider] logout() - guest token refetched:', !!token);
        });
      }
    },
    [onAuthChange, onSessionExpired, appBaseUrl],
  );

  // ============================================================================
  // Apply Authenticated Session
  // ============================================================================

  const applyAuthenticatedSession = useCallback(
    (data: LoginData) => {
      setState({
        isAuthenticated: true,
        isLoading: false,
        user: data.user,
        jwt: data.jwt,
        refreshToken: data.refreshToken,
        profileLoading: false,
        guestToken: null,
        guestTokenLoading: false,
        isRefreshing: false,
        tokenExpiryTime: getTokenExpiry(data.jwt),
        isTokenExpired: isTokenExpired(data.jwt),
      });
      onAuthChange?.(true);
      window.dispatchEvent(
        new CustomEvent('bolder-auth-change', {
          detail: {
            isAuthenticated: true,
            user: { fullName: data.user.fullName, email: data.user.email },
          },
        }),
      );
    },
    [onAuthChange],
  );

  // ============================================================================
  // Token Refresh Function
  // ============================================================================

  const refreshAuth = useCallback(async (): Promise<boolean> => {
    debugLog('[EmbedAuthProvider] refreshAuth() called');

    if (!appBaseUrl) return false;

    // Bridged-session path: the widget never owns a usable refresh token for
    // host iron-session users (the host rotates its own copy and POSTing it
    // to GraphQL would 500). Re-fetch /api/auth/session and apply the fresh
    // JWT. Shared with the GraphQL path via refreshState.promise so two
    // periodic-check ticks can't fire concurrent re-bridges.
    if (!state.refreshToken) {
      if (!state.isAuthenticated) return false;
      if (refreshState.promise) return refreshState.promise;
      const rebridgePromise = (async () => {
        try {
          const rebridged = await fetchHostSession(appBaseUrl);
          if (rebridged) {
            storeAuth(rebridged);
            applyAuthenticatedSession(rebridged);
            return true;
          }
          logout('session_expired');
          return false;
        } finally {
          refreshState.promise = null;
        }
      })();
      refreshState.promise = rebridgePromise;
      return rebridgePromise;
    }

    // Check if refresh is blocked due to too many failures
    if (isRefreshBlocked()) {
      console.warn('[EmbedAuth] Refresh blocked due to too many failures');
      return false;
    }

    // Prevent concurrent refresh requests (race condition prevention)
    if (refreshState.promise) {
      debugLog('[EmbedAuthProvider] refreshAuth() - already in progress, returning existing promise');
      return refreshState.promise;
    }

    // Mark as refreshing
    isRefreshingRef.current = true;
    setState((prev) => ({ ...prev, isRefreshing: true }));

    debugLog('[EmbedAuthProvider] refreshAuth() - starting refresh');

    // Create the refresh promise
    const refreshPromise = (async (): Promise<boolean> => {
      try {
        debugLog('[EmbedAuthProvider] refreshAuth() - calling refreshTokenApi');
        const result = await refreshTokenApi(appBaseUrl, state.refreshToken!, state.jwt || undefined);
        debugLog('[EmbedAuthProvider] refreshAuth() - result:', result.success, result.errorType);

        if (result.success && result.jwt) {
          // Success - update tokens
          const newData: LoginData = {
            jwt: result.jwt,
            refreshToken: result.refreshToken || state.refreshToken!,
            user: state.user!,
          };

          storeAuth(newData);
          recordRefreshSuccess();

          const newExpiry = getTokenExpiry(result.jwt);

          setState((prev) => ({
            ...prev,
            jwt: newData.jwt,
            refreshToken: newData.refreshToken,
            isRefreshing: false,
            tokenExpiryTime: newExpiry,
            isTokenExpired: false,
          }));

          isRefreshingRef.current = false;
          return true;
        }

        // Handle different error types
        if (result.errorType === 'expired_refresh_token') {
          // Refresh token is expired - must logout
          if (process.env.NODE_ENV !== 'production') {
            console.warn('[EmbedAuth] Refresh token expired - logging out');
          }
          logout('session_expired');
          isRefreshingRef.current = false;
          return false;
        }

        if (result.errorType === 'network') {
          // Network error - temporary, try again later
          const shouldLogout = recordRefreshFailure();
          if (shouldLogout) {
            logout('refresh_failed');
          } else {
            setState((prev) => ({ ...prev, isRefreshing: false }));
          }
          isRefreshingRef.current = false;
          return false;
        }

        // Other errors - record failure
        const shouldLogout = recordRefreshFailure();
        if (shouldLogout) {
          if (process.env.NODE_ENV !== 'production') {
            console.warn('[EmbedAuth] Max refresh failures reached - logging out');
          }
          logout('refresh_failed');
        } else {
          setState((prev) => ({ ...prev, isRefreshing: false }));
        }

        isRefreshingRef.current = false;
        return false;
      } catch {
        const shouldLogout = recordRefreshFailure();
        if (shouldLogout) {
          logout('refresh_failed');
        } else {
          setState((prev) => ({ ...prev, isRefreshing: false }));
        }
        isRefreshingRef.current = false;
        return false;
      }
    })();

    refreshState.promise = refreshPromise;
    return refreshPromise;
  }, [
    state.refreshToken,
    state.jwt,
    state.user,
    state.isAuthenticated,
    appBaseUrl,
    logout,
    applyAuthenticatedSession,
  ]);

  // ============================================================================
  // Token Status Check Function
  // ============================================================================

  const checkTokenStatus = useCallback(() => {
    if (!state.isAuthenticated || !state.jwt) return;

    const secondsUntilExpiry = getSecondsUntilExpiry(state.jwt);
    const expired = isTokenExpired(state.jwt);
    const expiringSoon = isTokenExpiringSoon(state.jwt);

    debugLog(
      '[EmbedAuthProvider] checkTokenStatus - expired:',
      expired,
      'expiringSoon:',
      expiringSoon,
      'secondsUntilExpiry:',
      secondsUntilExpiry,
    );

    // Check if token is expired
    if (expired) {
      debugLog('[EmbedAuthProvider] Token is expired, attempting refresh');
      setState((prev) => ({ ...prev, isTokenExpired: true }));
      // Try to refresh
      refreshAuth();
      return;
    }

    // Check if token is expiring soon
    if (expiringSoon) {
      debugLog('[EmbedAuthProvider] Token expiring soon, proactively refreshing');
      // Proactively refresh before expiry
      refreshAuth();
    }
  }, [state.isAuthenticated, state.jwt, refreshAuth]);

  // ============================================================================
  // Initialize Auth State
  // ============================================================================

  useEffect(() => {
    const init = async () => {
      const stored = getStoredAuth();

      if (stored && stored.jwt && stored.user) {
        applyAuthenticatedSession(stored);
        // Proactive refresh if the stored JWT has expired or is close to it.
        if (isTokenExpired(stored.jwt) || isTokenExpiringSoon(stored.jwt)) {
          setTimeout(() => refreshAuth(), 100);
        }
        return;
      }

      // No widget-side auth. Race the host-session bridge against a guest
      // token so logged-out users (the common case) don't pay an extra RTT.
      // Same-origin loaders that ARE logged in still finish faster than the
      // old sequential await chain.
      setState((prev) => ({ ...prev, guestTokenLoading: true }));
      const [bridged, guestToken] = await Promise.all([
        fetchHostSession(appBaseUrl),
        appBaseUrl ? fetchGuestToken(appBaseUrl) : Promise.resolve(null),
      ]);

      if (bridged) {
        storeAuth(bridged);
        applyAuthenticatedSession(bridged);
        return;
      }

      setState((prev) => ({
        ...prev,
        isLoading: false,
        guestToken,
        guestTokenLoading: false,
      }));
      onAuthChange?.(false);
      window.dispatchEvent(new CustomEvent('bolder-auth-change', { detail: { isAuthenticated: false } }));
    };

    init();
  }, [appBaseUrl]);

  // ============================================================================
  // Periodic Token Check
  // ============================================================================

  useEffect(() => {
    if (!state.isAuthenticated || !state.jwt) return;

    // Immediate check
    checkTokenStatus();

    // Set up periodic check
    const interval = setInterval(checkTokenStatus, TOKEN_CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [state.isAuthenticated, state.jwt, checkTokenStatus]);

  // ============================================================================
  // Page Visibility Handler
  // ============================================================================

  useEffect(() => {
    if (!state.isAuthenticated) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !visibilityCheckScheduled.current) {
        visibilityCheckScheduled.current = true;
        // Small delay to prevent multiple rapid checks
        setTimeout(() => {
          checkTokenStatus();
          visibilityCheckScheduled.current = false;
        }, 500);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [state.isAuthenticated, checkTokenStatus]);

  // ============================================================================
  // Login Function
  // ============================================================================

  const login = useCallback(
    (data: LoginData) => {
      storeAuth(data);
      resetRefreshState();
      applyAuthenticatedSession(data);
    },
    [applyAuthenticatedSession],
  );

  // ============================================================================
  // Refetch Guest Token
  // ============================================================================

  const refetchGuestToken = useCallback(async () => {
    if (!appBaseUrl) return;

    setState((prev) => ({ ...prev, guestTokenLoading: true }));
    const guestToken = await fetchGuestToken(appBaseUrl);
    setState((prev) => ({ ...prev, guestToken, guestTokenLoading: false }));
  }, [appBaseUrl]);

  // ============================================================================
  // Update User
  // ============================================================================

  const updateUser = useCallback((updates: Partial<User>) => {
    setState((prev) => {
      if (!prev.user) return prev;
      const updatedUser = { ...prev.user, ...updates };
      const stored = getStoredAuth();
      if (stored) {
        storeAuth({ ...stored, user: updatedUser });
      }
      return { ...prev, user: updatedUser };
    });
  }, []);

  // ============================================================================
  // Get Token (with auto-refresh check)
  // ============================================================================

  const getToken = useCallback((): string | null => {
    if (!state.jwt) return null;

    // Check if token needs refresh (non-blocking)
    if (isTokenExpiringSoon(state.jwt) && !isRefreshingRef.current) {
      refreshAuth();
    }

    return state.jwt;
  }, [state.jwt, refreshAuth]);

  // ============================================================================
  // Get Guest Token
  // ============================================================================

  const getGuestToken = useCallback((): string | null => {
    return state.guestToken;
  }, [state.guestToken]);

  // ============================================================================
  // Fetch Profile
  // ============================================================================

  const fetchProfile = useCallback(async (): Promise<void> => {
    if (!state.user?.id || !state.jwt || !appBaseUrl) return;

    setState((prev) => ({ ...prev, profileLoading: true }));

    try {
      const profile = await fetchUserProfile(appBaseUrl, state.user.id, state.jwt);
      if (profile) {
        const updatedUser: User = {
          ...state.user,
          fullName: profile.Name || state.user.fullName,
          email: profile.Email || state.user.email,
          displayPic: profile.DisplayPic,
          headline: profile.Headline,
          city: profile.City,
          country: profile.Country,
          handle: profile.Handle,
        };
        setState((prev) => ({ ...prev, user: updatedUser, profileLoading: false }));

        const stored = getStoredAuth();
        if (stored) {
          storeAuth({ ...stored, user: updatedUser });
        }
      } else {
        setState((prev) => ({ ...prev, profileLoading: false }));
      }
    } catch {
      setState((prev) => ({ ...prev, profileLoading: false }));
    }
  }, [state.user, state.jwt, appBaseUrl]);

  // ============================================================================
  // Context Value
  // ============================================================================

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    updateUser,
    refreshAuth,
    getToken,
    getGuestToken,
    fetchProfile,
    refetchGuestToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ============================================================================
// Hook
// ============================================================================

export const useEmbedAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useEmbedAuth must be used within EmbedAuthProvider');
  }
  return context;
};
